"use client";
import { AxiosInstance, AxiosRequestConfig } from "axios";
import { useFormikContext } from "formik";
import { get, isString, last, split } from "lodash";
import { Key, memo, useMemo } from "react";
import { FileInput, FileInputImage, FileInputProps } from "../components";

interface FileInputFieldProps {
  name: string;
  performAsyncUpload?: {
    axios: AxiosInstance;
    requestConfig: AxiosRequestConfig;
  };
}

function FileInputField({
  name,
  isMultiFile,
  performAsyncUpload,
  ...props
}: FileInputFieldProps & FileInputProps) {
  const { values, setFieldValue, setFieldError } = useFormikContext();
  const imageValues = useMemo(() => {
    let currentValue: FileInputImage[] = get(values, name, []) || [];
    if (isString(currentValue) && /(http(s?)):\/\//i.test(currentValue)) {
      currentValue = [
        {
          preview: currentValue,
          title: last(split(currentValue, "/")) || "",
        },
      ];
    }
    return currentValue;
  }, [values, name]);

  //this is done because the name field doesnot change when the image is dynamically rendered
  //and the key is used to force re-render whereas the name is set because we always donot have imageValues
  const imageKey = isString(imageValues[0]?.preview) ? imageValues : name;

  const handleAsyncChange = async (data: FileInputImage[]) => {
    const fileSize = data.reduce((total, fileImage) => {
      if (fileImage.file) {
        return total + fileImage.file.size;
      }
      return total;
    }, 0);
    if (fileSize === 0) {
      setFieldError(name, "The provided field is empty");
      return;
    }
    const formData = new FormData();
    if (isMultiFile) {
      data.forEach((file) => {
        file?.file && formData.append("file", file.file);
      });
    } else {
      data?.[0]?.file && formData.append("file", data[0].file);
    }
    // if (formData.get("file") === null) return;
    try {
      const response = await performAsyncUpload?.axios({
        ...performAsyncUpload.requestConfig,
        data: formData,
      });
      setFieldValue(name, response?.data?.data.file);
    } catch (err) {
      setFieldError(
        name,
        "Upload failed: Please check file size of the image. If the issue persists please contact support ",
      );
    }
  };

  const handleFileChange = (data: FileInputImage[]) => {
    if (isMultiFile) {
      const newValues = data.map((f) => f.file).filter(Boolean);
      setFieldValue(name, newValues);
    } else {
      data.length === 0
        ? setFieldValue(name, null)
        : setFieldValue(name, data[0]?.file);
    }
  };

  return (
    <FileInput
      {...props}
      key={imageKey as unknown as Key}
      isMultiFile={isMultiFile}
      initialValues={imageValues}
      onChange={performAsyncUpload ? handleAsyncChange : handleFileChange}
    />
  );
}

export default memo(FileInputField);
